import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

/**
 * @description
 * file or nativeAPIで端末 or カメラで画像を取得する
 * web/ios/android共通で使用できる（@capacitor/cameraを使用）
 *
 * @returns getImage 画像取得・カメラ起動APIを呼び出すメソッド。file形式で画像を返す。
 */
export const getImage = async (accepts = ['image/*'], type = 'Image') => {
  const image = await Camera.getPhoto({
    allowEditing: true,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Photos,
  })

  if (!image?.dataUrl) throw new Error('get photo failed')

  const [dataType, base64String] = image.dataUrl.split(';')

  // check image type
  const [_, imageType] = dataType.split(':')
  if (!accepts.some(accept => new RegExp(accept, 'i').test(imageType)))
    throw new Error(`uploadError${type}Only`)

  // generate random file name
  const fileName = `${Math.random()}`

  // convert base64 to File
  return convert4Base64toFile(
    base64String.split(',')[1],
    fileName,
    image.format
  )
}

/**
 * @description
 * file or nativeAPIで端末 or カメラで画像を取得する
 * web/ios/android共通で使用できる（@capacitor/cameraを使用）
 *
 * @returns pickImages 画像取得・カメラ起動APIを呼び出すメソッド。file形式で画像を返す。
 */
export const pickImages = async (
  accepts = ['image/*'],
  limit?: number
): Promise<File[]> => {
  const images = await Camera.pickImages({ limit })

  if (!images.photos.length) throw new Error('get photos failed')
  if (limit && images.photos.length > limit) throw new Error('uploadLimitError')

  // check image type
  if (
    images.photos.every(
      photo =>
        !accepts.some(accept =>
          new RegExp(accept, 'i').test(`image/${photo.format}`)
        )
    )
  ) {
    throw new Error('uploadErrorImageOnly')
  }

  // convert blob url array to File
  return Promise.all(
    images.photos.map(
      async value =>
        await convertBlobUrlToFile(
          value.webPath,
          // generate random file name
          Math.random().toString(),
          value.format
        )
    )
  )
}

/**
 * @description
 * base64からFileへ変換メソッド
 * [Reference] https://qiita.com/ns777/items/24f1a48fd9301ff3dcdb
 *
 * @param base64 base64 URL
 * @param fileName generate file name
 * @param fileType generate file type
 * @returns {File}
 */
const convert4Base64toFile = function (
  base64: string,
  fileName: string,
  fileType: string
) {
  // decode base64
  const bin = atob(base64.replace(/^.*,/, ''))
  // to binary
  const buffer = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i)
  }
  // create file object
  return new File([buffer.buffer], fileName, { type: fileType })
}

/**
 * @description
 * convert blobUrl to File
 *
 * @param blobUrl URL
 * @param fileName generate file name
 * @param fileType generate file type
 * @returns {File}
 */
const convertBlobUrlToFile = (
  blobUrl: string,
  fileName: string,
  fileType: string
) => {
  return fetch(blobUrl)
    .then(res => res.blob())
    .then(blob => new File([blob], fileName, { type: fileType }))
}
